import React, { useState, useRef, useEffect } from "react";
import { Stage, Layer, Line, Text, Rect, Circle } from "react-konva";

const Whiteboard = () => {
  const stageRef = useRef(null);
  const [tool, setTool] = useState("draw");
  const [color, setColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [eraserWidth, setEraserWidth] = useState(20); // new eraser size state
  const [penType, setPenType] = useState("pencil");
  const [lines, setLines] = useState([]);
  const isDrawing = useRef(false);
  const [isTyping, setIsTyping] = useState(false);
  const [textareaPos, setTextareaPos] = useState({ x: 0, y: 0 });
  const [textareaValue, setTextareaValue] = useState("");
  const [texts, setTexts] = useState([]);
  const [rectangles, setRectangles] = useState([]);
  const [circles, setCircles] = useState([]);
  const [tempRect, setTempRect] = useState(null);
  const [tempCircle, setTempCircle] = useState(null);
  const startPos = useRef(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const textareaRef = useRef(null);

  const handleCopy = () => {
    // Priority order: text > circle > rectangle > line
    if (texts.length > 0) {
      setClipboard({ type: "text", data: { ...texts[texts.length - 1] } });
    } else if (circles.length > 0) {
      setClipboard({
        type: "circle",
        data: { ...circles[circles.length - 1] },
      });
    } else if (rectangles.length > 0) {
      setClipboard({
        type: "rectangle",
        data: { ...rectangles[rectangles.length - 1] },
      });
    } else if (lines.length > 0) {
      setClipboard({ type: "line", data: { ...lines[lines.length - 1] } });
    }
  };

  const handlePaste = () => {
    if (!clipboard) return;
    saveStateForUndo(); // reuse from undo/redo

    const offset = 20; // paste slightly offset
    const item = clipboard.data;

    switch (clipboard.type) {
      case "text":
        setTexts([
          ...texts,
          { ...item, x: item.x + offset, y: item.y + offset },
        ]);
        break;
      case "circle":
        setCircles([
          ...circles,
          { ...item, x: item.x + offset, y: item.y + offset },
        ]);
        break;
      case "rectangle":
        setRectangles([
          ...rectangles,
          { ...item, x: item.x + offset, y: item.y + offset },
        ]);
        break;
      case "line":
        setLines([
          ...lines,
          {
            ...item,
            points: item.points.map((p, i) =>
              i % 2 === 0 ? p + offset : p + offset
            ),
          },
        ]);
        break;
      default:
        break;
    }
  };

  const saveStateForUndo = () => {
    setUndoStack((prev) => [
      ...prev,
      {
        lines,
        rectangles,
        circles,
        texts,
      },
    ]);
    setRedoStack([]); // clear redo history on new action
  };
  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setRedoStack((r) => [
      ...r,
      {
        lines,
        rectangles,
        circles,
        texts,
      },
    ]);
    setLines(previous.lines);
    setRectangles(previous.rectangles);
    setCircles(previous.circles);
    setTexts(previous.texts);
    setUndoStack((prev) => prev.slice(0, prev.length - 1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((u) => [
      ...u,
      {
        lines,
        rectangles,
        circles,
        texts,
      },
    ]);
    setLines(next.lines);
    setRectangles(next.rectangles);
    setCircles(next.circles);
    setTexts(next.texts);
    setRedoStack((prev) => prev.slice(0, prev.length - 1));
  };

  const handleMouseDown = (e) => {
    if (isTyping) return;
    saveStateForUndo(); // <--- ADD THIS

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();

    if (tool === "draw" || tool === "eraser") {
      isDrawing.current = true;
      setLines([
        ...lines,
        {
          points: [pos.x, pos.y],
          color: tool === "eraser" ? "rgba(0,0,0,1)" : color, // eraser color for destination-out
          strokeWidth: tool === "eraser" ? eraserWidth : strokeWidth, // use eraser width here
          penType,
          mode: tool, // "draw" or "eraser"
        },
      ]);
    } else if (tool === "text") {
      setTimeout(() => {
        setTextareaPos({ x: pos.x, y: pos.y });
        setTextareaValue("");
        setIsTyping(true);
      }, 0);
    } else if (tool === "rect") {
      startPos.current = pos;
      setTempRect({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
        stroke: color,
        strokeWidth,
      });
    } else if (tool === "circle") {
      startPos.current = pos;
      setTempCircle({
        x: pos.x,
        y: pos.y,
        radius: 0,
        stroke: color,
        strokeWidth,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isTyping) return;
    if (!isDrawing.current && tool !== "rect" && tool !== "circle") return;

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();

    if ((tool === "draw" || tool === "eraser") && isDrawing.current) {
      const lastLine = lines[lines.length - 1];
      lastLine.points = lastLine.points.concat([pos.x, pos.y]);
      const newLines = [...lines.slice(0, lines.length - 1), lastLine];
      setLines(newLines);
    } else if (tool === "rect" && tempRect && startPos.current) {
      const width = pos.x - startPos.current.x;
      const height = pos.y - startPos.current.y;
      setTempRect({ ...tempRect, width, height });
    } else if (tool === "circle" && tempCircle && startPos.current) {
      const radius = Math.sqrt(
        Math.pow(pos.x - startPos.current.x, 2) +
          Math.pow(pos.y - startPos.current.y, 2)
      );
      setTempCircle({ ...tempCircle, radius });
    }
  };

  const handleMouseUp = () => {
    if (isTyping) return;

    if (tool === "draw" || tool === "eraser") {
      isDrawing.current = false;
    } else if (tool === "rect" && tempRect) {
      setRectangles([...rectangles, tempRect]);
      setTempRect(null);
      startPos.current = null;
    } else if (tool === "circle" && tempCircle) {
      setCircles([...circles, tempCircle]);
      setTempCircle(null);
      startPos.current = null;
    }
  };

  const submitText = () => {
    if (textareaValue.trim()) {
      setTexts([
        ...texts,
        { x: textareaPos.x, y: textareaPos.y, text: textareaValue, color },
      ]);
    }
    setIsTyping(false);
    setTextareaValue("");
  };

  useEffect(() => {
    if (isTyping && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isTyping]);

  const clearCanvas = () => {
    if (window.confirm("Clear the canvas?")) {
      setLines([]);
      setTexts([]);
      setRectangles([]);
      setCircles([]);
      setIsTyping(false);
    }
  };

  const saveAsImage = () => {
    const uri = stageRef.current.toDataURL({ pixelRatio: 3 });
    const link = document.createElement("a");
    link.download = "whiteboard.png";
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRectDragEnd = (e, index) => {
    const newRects = rectangles.slice();
    newRects[index] = { ...newRects[index], x: e.target.x(), y: e.target.y() };
    setRectangles(newRects);
  };

  const handleCircleDragEnd = (e, index) => {
    const newCircles = circles.slice();
    newCircles[index] = {
      ...newCircles[index],
      x: e.target.x(),
      y: e.target.y(),
    };
    setCircles(newCircles);
  };

  const handleTextDragEnd = (e, index) => {
    const newTexts = texts.slice();
    newTexts[index] = { ...newTexts[index], x: e.target.x(), y: e.target.y() };
    setTexts(newTexts);
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Toolbar */}
      <div
        style={{
          width: "220px",
          background: "linear-gradient(to bottom right, #fdfbfb, #ebedee)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          boxShadow: "2px 0 10px rgba(0,0,0,0.15)",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <h3 style={{ marginBottom: 10, fontSize: "20px", color: "#333" }}>
          🛠️ Tools
        </h3>

        <select
          value={tool}
          onChange={(e) => {
            setTool(e.target.value);
            setIsTyping(false);
          }}
          style={{
            padding: "8px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            background: "#fff",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <option value="draw">Pen</option>
          <option value="text">Text</option>
          <option value="rect">Rectangle</option>
          <option value="circle">Circle</option>
        </select>

        <select
          value={penType}
          onChange={(e) => setPenType(e.target.value)}
          disabled={tool !== "draw"}
          style={{
            padding: "8px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            background: tool !== "draw" ? "#eee" : "#fff",
            color: tool !== "draw" ? "#888" : "#000",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <option value="pencil">Pencil</option>
          <option value="marker">Marker</option>
          <option value="highlighter">Highlighter</option>
        </select>

        <label style={{ fontSize: "14px", color: "#444" }}>
          🎨 Color:
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={tool === "eraser"}
            style={{
              marginLeft: "10px",
              border: "none",
              background: "transparent",
              cursor: tool === "eraser" ? "not-allowed" : "pointer",
            }}
          />
        </label>

        {tool === "draw" && (
          <label style={{ fontSize: "14px", color: "#444" }}>
            ✏️ Stroke Width:
            <input
              type="range"
              min="1"
              max="20"
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
              style={{ width: "100%", marginTop: 5 }}
            />
            <div style={{ fontSize: "12px", textAlign: "right" }}>
              {strokeWidth}
            </div>
          </label>
        )}

        {tool === "eraser" && (
          <label style={{ fontSize: "14px", color: "#444" }}>
            🧽 Eraser Size:
            <input
              type="range"
              min="5"
              max="50"
              value={eraserWidth}
              onChange={(e) => setEraserWidth(parseInt(e.target.value))}
              style={{ width: "100%", marginTop: 5 }}
            />
            <div style={{ fontSize: "12px", textAlign: "right" }}>
              {eraserWidth}
            </div>
          </label>
        )}

        <button
          onClick={clearCanvas}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#e74c3c",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#c0392b")}
          onMouseOut={(e) => (e.target.style.background = "#e74c3c")}
        >
          🗑️ Clear Canvas
        </button>
        <button
          onClick={handleUndo}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#f39c12",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#e67e22")}
          onMouseOut={(e) => (e.target.style.background = "#f39c12")}
        >
          ↩️ Undo
        </button>

        <button
          onClick={handleRedo}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#3498db",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#2980b9")}
          onMouseOut={(e) => (e.target.style.background = "#3498db")}
        >
          ↪️ Redo
        </button>

        <button
          onClick={handleCopy}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#8e44ad",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#732d91")}
          onMouseOut={(e) => (e.target.style.background = "#8e44ad")}
        >
          📋 Copy
        </button>

        <button
          onClick={handlePaste}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#2ecc71",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#27ae60")}
          onMouseOut={(e) => (e.target.style.background = "#2ecc71")}
        >
          📌 Paste
        </button>

        <button
          onClick={() => {
            if (tool === "eraser") {
              setTool("draw");
            } else {
              setTool("eraser");
            }
            setIsTyping(false);
          }}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: tool === "eraser" ? "#bdc3c7" : "#ecf0f1",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#d0d3d4")}
          onMouseOut={(e) =>
            (e.target.style.background =
              tool === "eraser" ? "#bdc3c7" : "#ecf0f1")
          }
        >
          ✂️ Eraser
        </button>

        <button
          onClick={saveAsImage}
          style={{
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            background: "#27ae60",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "bold",
            transition: "background 0.3s",
          }}
          onMouseOver={(e) => (e.target.style.background = "#219150")}
          onMouseOut={(e) => (e.target.style.background = "#27ae60")}
        >
          💾 Save as PNG
        </button>
      </div>

      {/* Canvas */}
      <div style={{ flexGrow: 1, background: "gray" }}>
        <Stage
          width={window.innerWidth - 200}
          height={window.innerHeight}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          ref={stageRef}
          style={{
            background: "white",
            cursor: tool === "eraser" ? "crosshair" : "default",
          }}
        >
          <Layer>
            <Rect
              x={0}
              y={0}
              width={window.innerWidth - 200}
              height={window.innerHeight}
              fill="white"
            />
            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.color}
                strokeWidth={
                  line.mode === "eraser"
                    ? line.strokeWidth
                    : line.penType === "pencil"
                      ? line.strokeWidth
                      : line.penType === "marker"
                        ? line.strokeWidth * 2
                        : line.penType === "highlighter"
                          ? line.strokeWidth * 3
                          : line.strokeWidth
                }
                tension={0.7} // smoothing for all lines
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  line.mode === "eraser" ? "destination-out" : "source-over"
                }
                opacity={line.penType === "highlighter" ? 0.3 : 1}
                shadowColor={line.penType === "marker" ? line.color : undefined}
                shadowBlur={line.penType === "marker" ? 4 : 0}
              />
            ))}

            {rectangles.map((rect, i) => (
              <Rect
                key={i}
                {...rect}
                draggable={!isTyping}
                onDragEnd={(e) => handleRectDragEnd(e, i)}
                onClick={() => {
                  if (tool === "eraser") {
                    const newRectangle = rectangles.filter(
                      (_, idx) => idx !== i
                    );
                    setRectangles(newRectangle);
                  }
                }}
              />
            ))}

            {circles.map((circle, i) => (
              <Circle
                key={i}
                {...circle}
                draggable={!isTyping}
                onDragEnd={(e) => handleCircleDragEnd(e, i)}
                onClick={() => {
                  if (tool === "eraser") {
                    const newCircles = circles.filter((_, idx) => idx !== i);
                    setCircles(newCircles);
                  }
                }}
              />
            ))}

            {tempRect && <Rect {...tempRect} dash={[4, 4]} />}

            {tempCircle && <Circle {...tempCircle} dash={[4, 4]} />}

            {texts.map((t, i) => (
              <Text
                key={i}
                x={t.x}
                y={t.y}
                text={t.text}
                fontSize={20}
                fill={t.color}
                draggable={!isTyping}
                onDragEnd={(e) => handleTextDragEnd(e, i)}
                onClick={() => {
                  if (tool === "eraser") {
                    const newTexts = texts.filter((_, idx) => idx !== i);
                    setTexts(newTexts);
                  }
                }}
              />
            ))}
          </Layer>
        </Stage>

        {isTyping && (
          <textarea
            ref={textareaRef}
            style={{
              position: "absolute",
              top: textareaPos.y,
              left: textareaPos.x + 200,
              fontSize: 20,
              border: "1px solid #ddd",
              padding: 4,
              resize: "none",
              outline: "none",
              backgroundColor: "white",
              color: color,
              minWidth: 100,
              minHeight: 30,
              zIndex: 1000,
            }}
            value={textareaValue}
            onChange={(e) => setTextareaValue(e.target.value)}
            onBlur={submitText}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitText();
              } else if (e.key === "Escape") {
                setIsTyping(false);
                setTextareaValue("");
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Whiteboard;
